/**
 * "Two-step verification for sensitive information" — the SITE switch, for site admins (2026-09-28).
 *
 * Mounted on /security, shown only to site admins (ADMIN / TENANT_MASTER_ADMIN / MASTER_ADMIN —
 * hide-don't-disable; the backend refuses everyone else anyway). When it is on, resident medical
 * records, leave medical certificates, payroll and pay rates, and bulk exports ask everyone who
 * opens them to confirm it's them with a second sign-in method. Off by default.
 *
 * - Shows how many staff already have two-step verification on (they can't open those pages until
 *   they do), in words, before the admin turns it on.
 * - Turning it on or off asks for the admin's OWN two-step verification first (the server demands
 *   a proof from the last 10 minutes), then a plain-words confirm.
 * - Nothing is reported as done until the server confirms it (hardening rule 16).
 */
import React from "react";
import { Box, Button, Heading, HStack, List, ListIcon, ListItem, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery } from "@apollo/client";
import { FiCheckCircle, FiShield } from "react-icons/fi";
import { SET_REQUIRE_TWO_FACTOR_FOR_SENSITIVE_DATA, TENANT_SECURITY_SETTINGS, TENANT_TWO_FACTOR_ADOPTION } from "../queries";
import type { TenantSecuritySettings, TenantTwoFactorAdoption } from "../../../generated/graphql";
import { errorWords, STEP_UP_FRESH_MINUTES } from "../utils/assurance";
import { useStepUp } from "../hooks/useStepUp";
import { AuthError, AuthLoading, AuthStatusPill, AuthSurface, AuthTip, alpha, useAuthConfirm, useAuthTheme } from "./ui";

/** Who sees this card. Mirrors the backend gate on setRequireTwoFactorForSensitiveData (+ MASTER_ADMIN bypass). */
export const SITE_ADMIN_PERMISSIONS = ["ADMIN", "TENANT_MASTER_ADMIN", "MASTER_ADMIN"] as const;
export const isSiteAdmin = (permissions?: readonly string[] | null): boolean =>
    !!permissions?.some((p) => (SITE_ADMIN_PERMISSIONS as readonly string[]).includes(p));

/** What the switch covers — shown to the admin in plain words. Keep in step with the backend list. */
export const SENSITIVE_AREAS = [
    "Resident medical records (files, access requests and the access log)",
    "Medical certificates attached to someone else's leave request",
    "Payroll, pay runs and staff pay rates, bank and tax details",
    "Bulk exports of staff or resident information",
] as const;

/** "12 of 40 staff have two-step verification on." — pure, unit-tested. */
export function adoptionLine(a?: Pick<TenantTwoFactorAdoption, "staffTotal" | "staffWithTwoFactor"> | null): string {
    if (!a) return "";
    if (a.staffTotal === 0) return "No staff accounts on this site yet.";
    const noun = a.staffTotal === 1 ? "staff member has" : "staff have";
    return `${a.staffWithTwoFactor} of ${a.staffTotal} ${noun} two-step verification on.`;
}

export const SiteSensitiveDataCard: React.FC = () => {
    const t = useAuthTheme();
    const toast = useToast();
    const { requireStepUp, withStepUp, stepUpDialog } = useStepUp({ maxAgeMinutes: STEP_UP_FRESH_MINUTES });
    const { confirm, confirmDialog } = useAuthConfirm();
    const settings = useQuery<{ tenantSecuritySettings: TenantSecuritySettings }>(TENANT_SECURITY_SETTINGS, { fetchPolicy: "network-only" });
    const adoption = useQuery<{ tenantTwoFactorAdoption: TenantTwoFactorAdoption }>(TENANT_TWO_FACTOR_ADOPTION, { fetchPolicy: "network-only" });
    const [setMut] = useMutation<{ setRequireTwoFactorForSensitiveData: TenantSecuritySettings }>(SET_REQUIRE_TWO_FACTOR_FOR_SENSITIVE_DATA, {
        refetchQueries: [{ query: TENANT_SECURITY_SETTINGS }],
    });
    const [busy, setBusy] = React.useState(false);

    const on = settings.data?.tenantSecuritySettings?.requireTwoFactorForSensitiveData === true;
    const a = adoption.data?.tenantTwoFactorAdoption;
    const without = a ? Math.max(0, a.staffTotal - a.staffWithTwoFactor) : null;

    const change = async (enabled: boolean) => {
        const ok = await confirm(
            enabled
                ? {
                    title: "Ask for two-step verification on sensitive information?",
                    body:
                        (without
                            ? `${without} staff ${without === 1 ? "member doesn't" : "don't"} have two-step verification on yet. They won't be able to open medical records, payroll or the other sensitive pages until they turn it on from their Security page. `
                            : "") +
                        "Everyone who opens those pages will be asked to confirm it's them with a second sign-in method, at most every 30 minutes.",
                    confirmLabel: "Turn it on",
                    tone: "warning",
                }
                : {
                    title: "Stop asking for two-step verification?",
                    body: "Medical records, payroll and the other sensitive pages will open with a single sign-in again. Permissions still apply.",
                    confirmLabel: "Turn it off",
                    tone: "danger",
                },
        );
        if (!ok) return;
        setBusy(true);
        try {
            if (!(await requireStepUp({ reason: "Changing this site's security setting needs your own two-step verification. Confirm it's you with a second sign-in method." }))) return;
            const res = await withStepUp(() => setMut({ variables: { enabled } }));
            const saved = res.data?.setRequireTwoFactorForSensitiveData;
            if (!saved || saved.requireTwoFactorForSensitiveData !== enabled) throw new Error("The server didn't confirm the change. Nothing was changed.");
            toast({
                title: enabled ? "Two-step verification is now asked for on sensitive information" : "Two-step verification is no longer asked for",
                status: "success",
                duration: 4000,
            });
        } catch (err) {
            toast({ title: "Couldn't change the setting", description: errorWords(err), status: "error", duration: 8000 });
        } finally {
            setBusy(false);
        }
    };

    const header = (
        <HStack justify="space-between" align="start" mb={3} flexWrap="wrap" gap={2}>
            <HStack spacing={3}>
                <Box as={FiShield} color={t.text} aria-hidden />
                <Heading size="sm" color={t.text}>Sensitive information on this site</Heading>
            </HStack>
            {settings.data && <AuthStatusPill kind="siteSensitive" status={on ? "ON" : "OFF"} />}
        </HStack>
    );

    let body: React.ReactNode;
    if (settings.loading && !settings.data) {
        body = <AuthLoading label="Checking this site's security settings" cards={1} columns={{ base: 1 }} />;
    } else if (settings.error || !settings.data) {
        body = <AuthError title="Couldn't load this site's security settings" error={settings.error || "No answer from the server."} onRetry={() => settings.refetch()} />;
    } else {
        const s = settings.data.tenantSecuritySettings;
        body = (
            <Stack spacing={4}>
                <Text fontSize="md" color={t.muted}>
                    For site admins. When this is on, everyone who opens these must confirm it's them with a second,
                    different sign-in method (at most every 30 minutes). Signing in itself doesn't change, and
                    permissions still decide who can see what.
                </Text>
                <List spacing={2} data-testid="site-sensitive-areas">
                    {SENSITIVE_AREAS.map((area) => (
                        <ListItem key={area} display="flex" alignItems="flex-start" fontSize="md" color={t.text}>
                            <ListIcon as={FiCheckCircle} color={t.faint} mt="3px" />
                            {area}
                        </ListItem>
                    ))}
                </List>

                <Box p={3} borderRadius="lg" bg={alpha(t.warning, 0.1)} borderWidth="1px" borderColor={alpha(t.warning, 0.3)} data-testid="site-sensitive-first">
                    <Text fontSize="md" color={t.text} fontWeight="semibold">Before you turn it on</Text>
                    <Text fontSize="md" color={t.text} mt={1}>
                        Staff who use these pages must first turn on two-step verification themselves: open their
                        Security page, set up a second sign-in method (for example a passkey as well as their password),
                        then press Turn on under Two-step verification. Until they do, those pages will send them there.
                    </Text>
                </Box>

                <Box data-testid="site-sensitive-adoption">
                    {adoption.loading && !a ? (
                        <Text fontSize="md" color={t.muted}>Counting staff with two-step verification…</Text>
                    ) : adoption.error ? (
                        <Text fontSize="md" color={t.muted}>Couldn't count staff with two-step verification right now.</Text>
                    ) : (
                        <Text fontSize="md" color={t.text}>{adoptionLine(a)}</Text>
                    )}
                </Box>

                {s.updatedAt && (
                    <Text fontSize="sm" color={t.muted} data-testid="site-sensitive-changed">
                        Last changed {new Date(s.updatedAt).toLocaleString()}{s.updatedByName ? ` by ${s.updatedByName}` : ""}.
                    </Text>
                )}

                <Stack direction={{ base: "column", sm: "row" }} spacing={3}>
                    {on ? (
                        <AuthTip label={"Stop asking for two-step verification on sensitive information.\n\nWhat happens when you click:\n  1. You're asked to confirm\n  2. You confirm it's you with a second sign-in method\n  3. The site setting is saved — this changes it for everyone"}>
                            <Button size="lg" minH="48px" w={{ base: "full", sm: "auto" }} variant="outline" colorScheme="red" isLoading={busy} loadingText="Saving…" onClick={() => void change(false)}>
                                Turn off for this site
                            </Button>
                        </AuthTip>
                    ) : (
                        <AuthTip label={"Ask for two-step verification on sensitive information.\n\nWhat happens when you click:\n  1. You're told how many staff don't have it yet\n  2. You confirm it's you with a second sign-in method\n  3. The site setting is saved — this changes it for everyone"}>
                            <Button size="lg" minH="48px" w={{ base: "full", sm: "auto" }} colorScheme="blue" isLoading={busy} loadingText="Saving…" onClick={() => void change(true)}>
                                Turn on for this site
                            </Button>
                        </AuthTip>
                    )}
                </Stack>
            </Stack>
        );
    }

    return (
        <AuthSurface data-testid="site-sensitive-card">
            {header}
            {body}
            {stepUpDialog}
            {confirmDialog}
        </AuthSurface>
    );
};

export default SiteSensitiveDataCard;
